import React, { useEffect, useRef, useState } from 'react';
import { StoryblokComponent } from 'storyblok-js-client';
import {
  TeaserService, PageItem, StringService, StoryblokService,
} from '../../services';
import { blokToComponent, ConditionalWrapper, filterProps } from '../helpers';
import { parseAssetSource } from '../props/asset';
import { Props } from '../types';

const renderWarning = (
  keyForChildren: string | number,
  slot: string,
  element: React.MutableRefObject<unknown>,
) => (
  <roche-teaser
    ref={element}
    key={keyForChildren}
    layout="teaser-variant-b-without-image"
    teaser-title="WARNING: This teaser is not published!"
    teaser-description="If you wish to proceed with this action, please publish the linked page first. Note that if the linked page on the teaser remains unpublished, this teaser will not show up on the live version of the page."
    loaded={true}
    slot={slot}
    unpublished={true}
  >
  </roche-teaser>
);
export const RocheTeaser = ({
  blok,
  updateHoistedState,
  wrapperTag,
  keyForChildren,
  slot,
  getComponent,
}: Props): JSX.Element => {
  const element = useRef(null);
  const [teaser, setTeaser] = useState<PageItem>({} as PageItem);
  const Wrapper = wrapperTag as React.ElementType;
  useEffect(() => {
    if (!blok.page?.story?.id) {
      updateHoistedState?.(0, 0, '', {});
      return;
    }
    TeaserService.getTeaser(blok.page.story.id)
      .then((result) => {
        if (!element.current) {
          return;
        }
        setTeaser(result);
        // eslint-disable-next-line no-underscore-dangle
        updateHoistedState?.(1, 1, blok._uid, result);
      });
  }, []);
  const { slotted } = filterProps(blok);
  const slottedComponents = Object.keys(slotted)
    .flatMap((key) => (slotted[key] as StoryblokComponent<string>[]).map(
      (component) => (component.component === 'roche-button'
        ? {
          ...component,
          slot: key,
          'no-margin': blok.layout.indexOf('teaser--card') === 0,
          appearance: 'tertiary',
        }
        : {
          ...component,
          slot: key,
        }),
    ));
  const teaserImage = teaser.content?.teaser_image?.[0];
  const isTeaserInDraft = Object.keys(teaser).length <= 0;

  return (
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
    >
      {
        isTeaserInDraft && StoryblokService.isInEditor()
          ? renderWarning(keyForChildren, slot, element)
          : <roche-teaser
            ref={element}
            key={keyForChildren}
            layout={blok.layout}
            teaser-image={teaserImage?.brightcove_id || parseAssetSource(teaserImage?.src.filename)}
            teaser-title={teaser.content?.teaser_title}
            teaser-overline={teaser.content?.teaser_overline}
            teaser-description={teaser.content?.teaser_description}
            teaser-date={teaser?.first_published_at}
            teaser-url={StringService.sanitizeUrl(`/${teaser?.full_slug}`)}
            loaded={teaser.content !== undefined}
            slot={slot}
            is-teaser-in-draft={isTeaserInDraft}
          >
            {slottedComponents.map(
              (component, index) => blokToComponent({ blok: { ...component }, getComponent }, `${index}`),
            )
            }
          </roche-teaser>
      }
    </ConditionalWrapper>
  );
};
